import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowPathIcon,
  ArrowRightIcon,
  CalendarDaysIcon,
  CheckCircleIcon,
  CreditCardIcon,
  LockClosedIcon,
  PencilSquareIcon,
  UserCircleIcon,
} from "@heroicons/react/24/outline";
import AccountPageShell from "../components/account/AccountPageShell";
import VotePurchaseHistory from "../components/VotePackages/VotePurchaseHistory";
import { ConfirmDialog } from "../components/admin";
import { checkSubscriptionStatus } from "../services/subscriptionService";

const EDITABLE_FIELDS = ["name", "lname", "username", "phone", "country"];

function Profile() {
  const [user, setUser] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    lname: "",
    username: "",
    email: "",
    phone: "",
    country: "",
  });
  const navigate = useNavigate();

  useEffect(() => {
    fetchUserProfile();
    fetchSubscriptionDetails();
    // Fetch each account resource once when the profile screen mounts.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isDirty =
    isEditing &&
    !!user &&
    EDITABLE_FIELDS.some((key) => (formData[key] || "") !== (user[key] || ""));

  useEffect(() => {
    if (!isDirty) return undefined;
    const warn = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [isDirty]);

  const fetchUserProfile = async () => {
    setIsLoading(true);
    setError("");
    try {
      const token = localStorage.getItem("token");
      const userId = localStorage.getItem("userId");
      if (!token || !userId) {
        navigate("/login");
        return;
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL}/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.status === 401) {
        clearSession();
        navigate("/login");
        return;
      }
      if (!response.ok) throw new Error("We couldn't load your profile. Please try again.");

      const data = await response.json();
      setUser(data);
      setFormData({
        name: data.name || "",
        lname: data.lname || "",
        username: data.username || "",
        email: data.email || "",
        phone: data.phone || "",
        country: data.country || "",
      });
    } catch (fetchError) {
      setError(fetchError.message || "We couldn't load your profile. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchSubscriptionDetails = async () => {
    try {
      const result = await checkSubscriptionStatus();
      if (result.subscription) setSubscription(result.subscription);
    } catch (subscriptionError) {
      // Subscription information is optional; keep the profile available if it fails.
      console.error("Error fetching subscription details:", subscriptionError);
    }
  };

  const clearSession = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("userRole");
    localStorage.removeItem("userId");
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData((previous) => ({ ...previous, [name]: value }));
  };

  const handleProfileUpdate = async (event) => {
    event.preventDefault();
    setError("");
    setSuccessMessage("");

    if (!formData.name.trim()) {
      setError("First name is required.");
      return;
    }

    setIsSaving(true);
    try {
      const token = localStorage.getItem("token");
      const userId = localStorage.getItem("userId");
      const response = await fetch(`${process.env.REACT_APP_API_URL}/users/${userId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          name: formData.name,
          lname: formData.lname,
          username: formData.username,
          phone: formData.phone,
          country: formData.country,
        }),
      });

      if (response.status === 401) {
        clearSession();
        navigate("/login");
        return;
      }
      if (!response.ok) {
        let message = "We couldn't save your changes. Please review your details and try again.";
        try {
          const responseData = await response.json();
          message = responseData.message || message;
        } catch {
          // Keep the safe, readable fallback if the server response has no JSON body.
        }
        throw new Error(message);
      }

      setUser((previous) => ({
        ...previous,
        name: formData.name.trim(),
        lname: formData.lname.trim(),
        username: formData.username.trim(),
        phone: formData.phone.trim(),
        country: formData.country.trim(),
      }));
      setSuccessMessage("Your profile has been updated.");
      setIsEditing(false);
    } catch (updateError) {
      setError(updateError.message || "We couldn't save your changes. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const startEditing = () => {
    setFormData({
      name: user.name || "",
      lname: user.lname || "",
      username: user.username || "",
      email: user.email || "",
      phone: user.phone || "",
      country: user.country || "",
    });
    setError("");
    setSuccessMessage("");
    setIsEditing(true);
  };

  const cancelEditing = () => {
    if (isDirty) {
      setShowDiscardConfirm(true);
      return;
    }
    setIsEditing(false);
    setError("");
  };

  const discardChanges = () => {
    setShowDiscardConfirm(false);
    setIsEditing(false);
    setError("");
  };

  const displayName = [user?.name, user?.lname].filter(Boolean).join(" ");
  const initials = [user?.name, user?.lname]
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase())
    .join("") || "U";
  const memberSince = user?.createdAt
    ? new Date(user.createdAt).toLocaleDateString(undefined, { year: "numeric", month: "long" })
    : null;
  const subscriptionDuration = {
    Daily: "1 day",
    Weekly: "7 days",
    Monthly: "30 days",
    "3 Months": "90 days",
  }[subscription?.plan?.duration] || subscription?.plan?.duration;

  return (
    <AccountPageShell className="pb-12">
        <header className="border-b border-line bg-surface-900">
          <div className="mx-auto flex max-w-7xl flex-col gap-5 px-5 py-7 sm:px-8 sm:py-9 lg:flex-row lg:items-end lg:justify-between lg:px-10">
            <div>
              <p className="eyebrow-accent inline-flex items-center gap-2">
                <UserCircleIcon aria-hidden="true" className="h-4 w-4" /> ACCOUNT
              </p>
              <h1 className="mt-2 text-3xl font-bold tracking-tight text-white sm:text-4xl">Your profile</h1>
              <p className="mt-2 max-w-xl text-sm leading-6 text-gray-400 sm:text-base">
                Manage your personal details, membership, and account security.
              </p>
            </div>
            {user && !isEditing && (
              <button type="button" onClick={startEditing} className="btn-primary w-full gap-2 sm:w-auto">
                <PencilSquareIcon aria-hidden="true" className="h-4 w-4" /> Edit profile
              </button>
            )}
          </div>
        </header>

        <div className="mx-auto max-w-7xl space-y-5 px-5 py-6 sm:px-8 sm:py-8 lg:px-10">
          {isLoading ? (
            <section role="status" aria-label="Loading your profile" className="flex min-h-72 items-center justify-center gap-3 rounded-xl border border-line bg-surface-900 text-sm text-gray-300">
              <span className="h-5 w-5 animate-spin rounded-full border-2 border-line-strong border-t-accent" />
              Loading your account…
            </section>
          ) : error && !user ? (
            <section role="alert" className="rounded-xl border border-line bg-surface-900 px-5 py-12 text-center sm:px-8">
              <UserCircleIcon aria-hidden="true" className="mx-auto h-10 w-10 text-gray-500" />
              <h2 className="mt-4 text-lg font-semibold text-white">Profile unavailable</h2>
              <p className="mx-auto mt-2 max-w-md text-sm text-gray-400">{error}</p>
              <button type="button" onClick={fetchUserProfile} className="btn-secondary mt-5 gap-2">
                <ArrowPathIcon aria-hidden="true" className="h-4 w-4" /> Try again
              </button>
            </section>
          ) : (
            <>
              <section className="overflow-hidden rounded-xl border border-line bg-surface-850">
                <div className="flex flex-col gap-5 p-5 sm:flex-row sm:items-center sm:justify-between sm:p-7">
                  <div className="flex min-w-0 items-center gap-4 sm:gap-5">
                    <div aria-hidden="true" className="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl border border-accent/30 bg-accent/10 text-xl font-bold tracking-wide text-accent-soft sm:h-20 sm:w-20 sm:text-2xl">
                      {initials}
                    </div>
                    <div className="min-w-0">
                      <p className="eyebrow">ACCOUNT PROFILE</p>
                      <h2 className="mt-1 truncate text-xl font-semibold tracking-tight text-white sm:text-2xl">
                        {displayName || "Your account"}
                      </h2>
                      <p className="mt-1 truncate text-sm text-gray-400">
                        {user?.username ? `@${user.username}` : user?.email || "Personal account"}
                      </p>
                      {memberSince && (
                        <p className="mt-2 inline-flex items-center gap-1.5 text-xs text-gray-500">
                          <CalendarDaysIcon aria-hidden="true" className="h-4 w-4" /> Member since {memberSince}
                        </p>
                      )}
                    </div>
                  </div>
                  {subscription && (
                    <div className="flex items-center gap-3 self-start rounded-lg border border-line bg-surface-900 px-4 py-3 sm:self-center">
                      <span className={`h-2.5 w-2.5 rounded-full ${subscription.active ? "bg-emerald-400" : "bg-gray-500"}`} />
                      <div>
                        <p className="eyebrow">MEMBERSHIP</p>
                        <p className="mt-0.5 text-sm font-semibold text-white">
                          {subscription.plan?.planName || "Standard"} · {subscription.active ? "Active" : "Inactive"}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </section>

              {error && (
                <div role="alert" className="rounded-lg border border-red-900/60 bg-red-950/30 px-4 py-3 text-sm text-red-200">
                  {error}
                </div>
              )}
              {successMessage && (
                <div role="status" aria-live="polite" className="flex items-center gap-2 rounded-lg border border-emerald-900/50 bg-emerald-950/20 px-4 py-3 text-sm text-emerald-200">
                  <CheckCircleIcon aria-hidden="true" className="h-5 w-5 shrink-0" /> {successMessage}
                </div>
              )}

              {isEditing ? (
                <section className="rounded-xl border border-line bg-surface-850">
                  <div className="border-b border-line px-5 py-4 sm:px-6">
                    <h2 className="text-lg font-semibold text-white">Personal information</h2>
                    <p className="mt-1 text-sm text-gray-400">Update the details associated with your account.</p>
                  </div>
                  <form onSubmit={handleProfileUpdate} className="p-5 sm:p-6">
                    <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                      <FormField id="profile-first-name" label="First name" required>
                        <input id="profile-first-name" type="text" name="name" autoComplete="given-name" value={formData.name} onChange={handleInputChange} required className="field" />
                      </FormField>
                      <FormField id="profile-last-name" label="Last name">
                        <input id="profile-last-name" type="text" name="lname" autoComplete="family-name" value={formData.lname} onChange={handleInputChange} className="field" />
                      </FormField>
                      <FormField id="profile-username" label="Username">
                        <input id="profile-username" type="text" name="username" autoComplete="username" value={formData.username} onChange={handleInputChange} className="field" />
                      </FormField>
                      <FormField id="profile-email" label="Email address" hint="Contact support if you need to change your email.">
                        <input id="profile-email" type="email" name="email" autoComplete="email" value={formData.email} readOnly aria-readonly="true" className="field cursor-not-allowed text-gray-400" />
                      </FormField>
                      <FormField id="profile-phone" label="Phone number">
                        <input id="profile-phone" type="tel" name="phone" autoComplete="tel" value={formData.phone} onChange={handleInputChange} className="field" />
                      </FormField>
                      <FormField id="profile-country" label="Country">
                        <input id="profile-country" type="text" name="country" autoComplete="country-name" value={formData.country} onChange={handleInputChange} className="field" />
                      </FormField>
                    </div>
                    <div className="mt-6 flex flex-col-reverse gap-3 border-t border-line pt-5 sm:flex-row sm:justify-end">
                      <button type="button" onClick={cancelEditing} disabled={isSaving} className="btn-secondary">Cancel</button>
                      <button type="submit" disabled={isSaving} className="btn-primary gap-2">
                        {isSaving && <span aria-hidden="true" className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />}
                        {isSaving ? "Saving changes…" : "Save changes"}
                      </button>
                    </div>
                  </form>
                </section>
              ) : (
                <section className="rounded-xl border border-line bg-surface-850">
                  <div className="border-b border-line px-5 py-4 sm:px-6">
                    <h2 className="text-lg font-semibold text-white">Personal information</h2>
                    <p className="mt-1 text-sm text-gray-400">Your account details and contact information.</p>
                  </div>
                  <dl className="grid grid-cols-1 divide-y divide-line sm:grid-cols-2 sm:divide-y-0 sm:px-6 sm:py-2">
                    <ProfileDetail label="First name" value={user?.name} />
                    <ProfileDetail label="Last name" value={user?.lname} />
                    <ProfileDetail label="Username" value={user?.username ? `@${user.username}` : null} />
                    <ProfileDetail label="Email address" value={user?.email} />
                    <ProfileDetail label="Phone number" value={user?.phone} />
                    <ProfileDetail label="Country" value={user?.country} />
                  </dl>
                </section>
              )}

              {subscription && (
                <section className="rounded-xl border border-line bg-surface-850">
                  <div className="flex flex-col gap-3 border-b border-line px-5 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6">
                    <div>
                      <h2 className="text-lg font-semibold text-white">Subscription</h2>
                      <p className="mt-1 text-sm text-gray-400">Your current plan and account benefits.</p>
                    </div>
                    {!subscription.active && (
                      <button type="button" onClick={() => navigate("/subscription-plans")} className="btn-secondary gap-2">
                        View plans <ArrowRightIcon aria-hidden="true" className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                  <dl className="grid grid-cols-1 gap-x-6 sm:grid-cols-2 lg:grid-cols-3">
                    <ProfileDetail label="Plan" value={subscription.plan?.planName || "Standard"} />
                    <ProfileDetail label="Duration" value={subscriptionDuration} />
                    <ProfileDetail label="Status" value={subscription.active ? "Active" : "Inactive"} />
                    <ProfileDetail label="Price" value={subscription.plan?.price != null ? `ZMW ${subscription.plan.price}` : null} />
                    <ProfileDetail label="Start date" value={formatDate(subscription.startDate)} />
                    <ProfileDetail label="Expiry date" value={formatDate(subscription.endDate)} />
                    <ProfileDetail label="Reference ID" value={subscription.referenceId} />
                    <ProfileDetail label="Votes remaining" value={subscription.votesRemaining != null ? Number(subscription.votesRemaining).toLocaleString() : null} />
                  </dl>
                </section>
              )}

              <section className="rounded-xl border border-line bg-surface-850">
                <div className="flex flex-col gap-4 p-5 sm:flex-row sm:items-center sm:justify-between sm:p-6">
                  <div className="flex items-start gap-3">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-line bg-surface-900 text-gray-300">
                      <LockClosedIcon aria-hidden="true" className="h-5 w-5" />
                    </div>
                    <div>
                      <h2 className="text-base font-semibold text-white">Password & security</h2>
                      <p className="mt-1 max-w-lg text-sm leading-6 text-gray-400">
                        Use the secure password reset flow to update your account password. Your password is never shown here.
                      </p>
                    </div>
                  </div>
                  <button type="button" onClick={() => navigate("/forgot-password")} className="btn-secondary w-full gap-2 sm:w-auto">
                    Reset password <ArrowRightIcon aria-hidden="true" className="h-4 w-4" />
                  </button>
                </div>
              </section>

              <section aria-labelledby="purchase-history-heading" className="space-y-3">
                <div className="flex items-center gap-3 px-1">
                  <CreditCardIcon aria-hidden="true" className="h-5 w-5 text-accent-soft" />
                  <div>
                    <h2 id="purchase-history-heading" className="text-lg font-semibold text-white">Vote credit purchases</h2>
                    <p className="mt-0.5 text-sm text-gray-400">A record of your recent vote package purchases.</p>
                  </div>
                </div>
                <VotePurchaseHistory />
                <button type="button" onClick={() => navigate("/buy-votes")} className="btn-ghost gap-2 px-1 text-accent-soft hover:text-white">
                  Buy vote credits <ArrowRightIcon aria-hidden="true" className="h-4 w-4" />
                </button>
              </section>
            </>
          )}
        </div>

        {showDiscardConfirm && (
          <ConfirmDialog
            title="Discard your changes?"
            message="Any edits you have made will be lost. Do you want to leave without saving?"
            confirmLabel="Discard changes"
            onConfirm={discardChanges}
            onCancel={() => setShowDiscardConfirm(false)}
          />
        )}
    </AccountPageShell>
  );
}

function FormField({ id, label, required = false, hint, children }) {
  return (
    <div>
      <label htmlFor={id} className="field-label">
        {label}{required && <span className="ml-1 text-accent-soft" aria-hidden="true">*</span>}
      </label>
      {children}
      {hint && <p className="mt-1.5 text-xs leading-5 text-gray-500">{hint}</p>}
    </div>
  );
}

function ProfileDetail({ label, value }) {
  return (
    <div className="min-w-0 px-5 py-4 sm:px-0">
      <dt className="text-xs font-medium uppercase tracking-wider text-gray-500">{label}</dt>
      <dd className="mt-1 break-words text-sm font-medium text-gray-200">{value || <span className="font-normal text-gray-500">Not provided</span>}</dd>
    </div>
  );
}

function formatDate(value) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

export default Profile;
